import { useEffect, useState } from "react";

import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import EditIcon from "@mui/icons-material/EditOutlined";

import { IOccupant } from "../../../../../types/ILocation";
import FormField from "../../../../../components/FormField";
import { useNitroStore } from "../../../../../store/Store";

const colors: any = {
  LIQUID: [
    [0, 160, 223],
    [0, 92, 166],
  ],
  DRY: [
    [223, 147, 0],
    [163, 108, 0],
  ],
  OTHER: [
    [0, 166, 166],
    [0, 140, 140],
  ],
};

const getRGB = (start: number[]) => {
  return `rgb(${start.join(",")})`;
};

const ProductFormsGraph = ({
  productForm,
  products,
  total,
  uom,
}: {
  productForm: string;
  products: {
    product: string;
    storage: { occupant: string; quantity: number }[];
  }[];
  total: number;
  uom: string;
}) => {
  return (
    <div
      style={{
        border: "1px solid lightgray",
        borderRadius: 4,
        padding: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexGrow: 1,
            gap: 30,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 18,
              color: "#0F2C52",
              fontFamily: "TradeGothic LT Cond20",
            }}
          >
            {productForm}
          </div>
          <div style={{ color: "#4b4b4b", fontSize: 11 }}>
            <b>Total: </b>
            {`${total.toLocaleString()} ${uom}`}
          </div>
        </div>

        {/* <InfoPopper message={infoMessage} /> */}
      </div>
      <div
        style={{
          width: "100%",
          paddingTop: 8,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {products.map((product) => {
          const sum = product.storage.reduce(
            (acc, curr) => acc + curr.quantity,
            0,
          );

          const [first, last] = colors[productForm]
            ? colors[productForm]
            : colors.OTHER;

          const productFormColors: string[] = [];
          const colorCount = product.storage.length;
          if (colorCount === 1) {
            productFormColors.push(getRGB(first));
          } else if (colorCount === 2) {
            productFormColors.push(getRGB(first));
            productFormColors.push(getRGB(last));
          } else {
            productFormColors.push(getRGB(first));
            const step = 1 / (colorCount - 1);
            for (let i = 1; i < colorCount - 1; i += 1) {
              const r = Math.floor(first[0] + (last[0] - first[0]) * i * step);
              const g = Math.floor(first[1] + (last[1] - first[1]) * i * step);
              const b = Math.floor(first[2] + (last[2] - first[2]) * i * step);
              productFormColors.push(getRGB([r, g, b]));
            }

            productFormColors.push(getRGB(last));
          }

          return (
            <div
              style={{ display: "flex", alignItems: "center", height: 40 }}
              key={`Product-${product.product}-${productForm}`}
            >
              <div
                style={{
                  paddingRight: 10,
                  width: 90,
                  maxWidth: 90,
                  minWidth: 90,
                  textAlign: "start",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#595959",
                  }}
                >
                  {product.product}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 300,
                    color: "#595959",
                  }}
                >
                  {`${sum.toLocaleString()} ${uom}`}
                </div>
              </div>
              <div
                style={{
                  width: "100%",
                  margin: 0,
                  padding: 0,
                  height: 40,
                  maxHeight: 40,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    height: 40,
                    alignItems: "center",
                  }}
                >
                  {sum > 0 &&
                    product.storage.map((storage, index) => {
                      let borderRadius: string | undefined = "0px";
                      if (index === 0 && product.storage.length === 1) {
                        borderRadius = "4px";
                      } else if (index === 0) {
                        borderRadius = "4px 0px 0px 4px";
                      } else if (index === product.storage.length - 1) {
                        borderRadius = "0px 4px 4px 0px";
                      }

                      const percent = `${(storage.quantity / sum) * 100}%`;

                      return (
                        <Tooltip
                          key={`Tooltip-${product.product}-${storage.occupant}`}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "white",
                                border: `2px solid ${productFormColors[index]}`,
                              },
                            },
                          }}
                          title={
                            <div
                              style={{
                                padding: 10,
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                              }}
                            >
                              <FormField label="Name">
                                <div
                                  style={{
                                    color: "#595959",
                                    fontWeight: 300,
                                    fontSize: 13,
                                  }}
                                >
                                  {storage.occupant}
                                </div>
                              </FormField>
                              <FormField label="Quantity">
                                <div
                                  style={{
                                    color: "#595959",
                                    fontWeight: 300,
                                    fontSize: 13,
                                  }}
                                >{`${storage.quantity} ${uom}`}</div>
                              </FormField>
                            </div>
                          }
                        >
                          <div
                            style={{
                              width: percent,
                              height: "100%",
                              color: "white",
                              backgroundColor:
                                productFormColors[index] || "red",
                              borderRadius,
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OverviewProductStorageTab = ({
  occupants,
  uom,
}: {
  occupants: IOccupant[];
  uom: string;
}) => {
  const [setInsightOverviewTab, setInsightsEditing] = useNitroStore((state) => [
    state.setInsightOverviewTab,
    state.setInsightsEditing,
  ]);

  const [formGroups, setFormGroups] = useState<{
    [productForm: string]: {
      product: string;
      productForm: string;
      storage: { occupant: string; quantity: number }[];
    }[];
  }>({});

  useEffect(() => {
    const products: {
      product: string;
      productForm: string;
      storage: { occupant: string; quantity: number }[];
    }[] = occupants.reduce((acc: any, curr: IOccupant) => {
      curr.products.forEach(({ product, productForm, quantity }) => {
        const index = acc.findIndex((item: any) => item.product === product);

        if (index === -1) {
          acc.push({
            product,
            productForm,

            storage: [
              {
                occupant: curr.name,
                quantity: quantity || 0,
              },
            ],
          });
        } else {
          acc[index].storage.push({
            occupant: curr.name,
            quantity: quantity || 0,
          });
        }
      });

      return [...acc];
    }, []);

    const newFormGroups = products.reduce((acc: any, curr) => {
      if (!acc[curr.productForm]) {
        acc[curr.productForm] = [];
      }

      acc[curr.productForm].push(curr);

      return acc;
    }, {});

    setFormGroups(newFormGroups);
  }, [occupants]);

  return (
    <div
      style={{
        flexGrow: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="insightsPanelPadding"
        style={{
          height: 32.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 10,
        }}
      >
        <div
          style={{
            color: "#0F2C52",
            fontWeight: 700,
            fontSize: 18,
            textTransform: "uppercase",
          }}
        >
          Facility products
        </div>
        <Button
          id="productStorageEditButton"
          variant="contained"
          onClick={() => {
            setInsightsEditing("Occupants");
            setInsightOverviewTab("Occupants");
          }}
          style={{ display: "flex", gap: 4 }}
        >
          <EditIcon style={{ fontSize: 18 }} />
          Edit
        </Button>
      </div>
      <div
        className="insightsPanelPadding"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 20,
          marginBottom: 20,
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        {(!formGroups || Object.keys(formGroups).length === 0) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 15,
            }}
          >
            <div style={{ textAlign: "center", color: "#595959" }}>
              This facility currently has no saved products.
            </div>

            <Button
              id="productStorageAddProductButton"
              style={{ width: 140 }}
              variant="contained"
              onClick={() => {
                setInsightsEditing("Occupants");
                setInsightOverviewTab("Occupants");
              }}
            >
              Add products
            </Button>
          </div>
        )}

        {Object.keys(formGroups).map((productForm) => {
          const products = formGroups[productForm];

          const total = products.reduce((acc, curr) => {
            return acc + curr.storage.reduce((a, c) => a + c.quantity, 0);
          }, 0);

          return (
            <div style={{ width: "100%" }} key={productForm}>
              <ProductFormsGraph
                products={products}
                total={total}
                uom={uom}
                productForm={productForm}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OverviewProductStorageTab;
